import React, { useState } from 'react';
import { Heart, Send, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { eventData } from '../mockData';

const RECIPIENT_EMAIL = 'rosedelimburgstirum@gmail.com';

const Remerciement = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({ name: '', message: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.message.trim()) {
      toast({
        title: 'Champs manquants',
        description: 'Veuillez remplir votre nom et votre message',
        variant: 'destructive',
      });
      return;
    }

    const subject = encodeURIComponent(`Message de remerciement de ${formData.name} - Fury Rose`);
    const body = encodeURIComponent(
      `Bonjour Rose,\n\n${formData.message}\n\nAvec toute ma gratitude,\n${formData.name}`
    );

    const mailtoLink = `mailto:${RECIPIENT_EMAIL}?subject=${subject}&body=${body}`;
    window.location.href = mailtoLink;

    toast({
      title: 'Message prêt à envoyer !',
      description: 'Votre messagerie va s\'ouvrir. Cliquez sur "Envoyer" pour finaliser.',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-neutral-900 to-black py-20 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <Heart className="w-16 h-16 text-orange-500 mx-auto mb-4" fill="currentColor" />
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-black mb-6 text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-amber-500 to-orange-600 break-words">
            Remerciements
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Envie de partager un mot doux à l'organisatrice ? Laissez-lui votre message ici. 🌹
          </p>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          data-testid="thanks-form"
          className="bg-neutral-800/50 backdrop-blur-sm p-8 md:p-12 rounded-xl border-2 border-orange-600/30 space-y-6"
        >
          <div>
            <Label htmlFor="name" className="text-orange-400 font-bold mb-2 block">
              Votre nom *
            </Label>
            <Input
              id="name"
              name="name"
              data-testid="thanks-name-input"
              value={formData.name}
              onChange={handleChange}
              placeholder="Ex: Max Rockatansky"
              className="bg-black/60 border-orange-600/50 text-white focus:border-orange-500"
              required
            />
          </div>

          <div>
            <Label htmlFor="message" className="text-orange-400 font-bold mb-2 block">
              Votre message *
            </Label>
            <Textarea
              id="message"
              name="message"
              data-testid="thanks-message-textarea"
              value={formData.message}
              onChange={handleChange}
              rows={6}
              placeholder="Merci pour cette soirée incroyable..."
              className="bg-black/60 border-orange-600/50 text-white focus:border-orange-500 resize-none"
              required
            />
          </div>

          <div className="bg-black/40 p-4 rounded-lg border border-orange-500/20 flex items-start gap-3">
            <Mail className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-400">
              En cliquant sur "Envoyer", votre application de messagerie s'ouvrira avec votre message pré-rempli, prêt à être envoyé à <span className="text-orange-400 font-semibold">{eventData.contact.organizer}</span>.
            </p>
          </div>

          <Button
            type="submit"
            size="lg"
            data-testid="thanks-submit-btn"
            className="w-full bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white font-bold text-lg py-6 border-2 border-orange-400 shadow-lg shadow-orange-500/50 transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2"
          >
            <Send className="w-5 h-5" />
            Envoyer mon message
          </Button>
        </form>

        {/* Signature */}
        <div className="text-center mt-10">
          <p className="text-gray-400 italic">
            Chaque message est lu avec beaucoup d'amour. 💛
          </p>
        </div>
      </div>
    </div>
  );
};

export default Remerciement;
